import clsx from 'clsx';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';

import IconTextAlignLeft from '@/assets/icons/icon-text-align-left.svg?react';
import IconTextAlignCenter from '@/assets/icons/icon-text-align-center.svg?react';
import IconTextAlignRight from '@/assets/icons/icon-text-align-right.svg?react';
import IconTextAlignLeftFilled from '@/assets/icons/icon-text-align-left-filled.svg?react';
import IconTextAlignCenterFilled from '@/assets/icons/icon-text-align-center-filled.svg?react';
import IconTextAlignRightFilled from '@/assets/icons/icon-text-align-right-filled.svg?react';

interface AlignFieldProps {
  text: string;
  align?: 'left' | 'center' | 'right';
  onClick?: (size: 'left' | 'center' | 'right') => void;
  className?: string;
}

const sizes: Array<'left' | 'center' | 'right'> = ['left', 'center', 'right'];

export const AlignField: React.FC<AlignFieldProps> = ({
  align = 'left',
  onClick,
  className,
  text = 'Align',
}) => {
  const [selectedSize, setSelectedSize] = useState(align);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);

  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({
    left: null,
    center: null,
    right: null,
  });

  useEffect(() => {
    if (align !== selectedSize) {
      setSelectedSize(align);
    }
  }, [align]);

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedSize];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [selectedSize]);

  const handleClick = (newSize: 'left' | 'center' | 'right') => {
    setIsAnimated(true);
    setSelectedSize(newSize);
    if (onClick) onClick(newSize);
  };

  return (
    <div className={clsx('flex items-center justify-between gap-4', className)}>
      {text && <p className="text-button-sm font-medium text-neutral-500">{text}</p>}

      <div className="relative flex h-8 items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
        <div
          className={`absolute top-0 z-20 h-full rounded-md bg-[#fff] outline outline-offset-[-1px] outline-[#e9ecf0] ${isAnimated ? 'transition-all duration-300' : ''}`}
          style={{
            width: `${highlightStyle.width}px`,
            transform: `translateX(${highlightStyle.left}px)`,
          }}
        />

        {sizes.map((sz) => {
          const Icon =
            sz === 'left'
              ? selectedSize === sz
                ? IconTextAlignLeftFilled
                : IconTextAlignLeft
              : sz === 'center'
                ? selectedSize === sz
                  ? IconTextAlignCenterFilled
                  : IconTextAlignCenter
                : selectedSize === sz
                  ? IconTextAlignRightFilled
                  : IconTextAlignRight;

          return (
            <button
              key={sz}
              ref={(el) => {
                optionRefs.current[sz] = el;
              }}
              onClick={() => handleClick(sz)}
              className="relative z-20 flex h-8 w-8 cursor-pointer items-center justify-center"
              data-testid={`align-${sz}`}
              aria-label={`Align ${sz}`}
            >
              <Icon className="h-4.5 w-4.5 text-neutral-300" />
            </button>
          );
        })}
      </div>
    </div>
  );
};
